<script setup lang="ts">
import { ProposedDelivery } from '@nuts/auto-delivery-sdk';
import { useElementBounding, whenever } from '@vueuse/core';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { ref } from 'vue';

import AutoDeliveryDeliveriesList from '@/components/auto-delivery/AutoDeliveryDeliveriesList.vue';
import AutoDeliveryIntervalSelector from '@/components/auto-delivery/AutoDeliveryIntervalSelector.vue';
import RadioGroupItem from '@/components/base/form/RadioGroupItem.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';

dayjs.extend(advancedFormat);

const props = defineProps<{
  isLoading?: boolean;
  nextDeliveries?: ProposedDelivery[];
  showUpsellOption?: boolean;
}>();

const interval = defineModel<number>({ required: true });

const emit = defineEmits<{
  'add-to-existing-delivery': [delivery?: ProposedDelivery, oneTime?: boolean];
  'learn-more': [];
  'start-auto-delivery': [];
}>();

const footerContainer = ref<HTMLElement>();
const option = ref<'new' | 'upsell' | ProposedDelivery>(
  props.nextDeliveries?.length ? props.nextDeliveries[0] : 'new',
);
const oneTimeDelivery = ref(false);

const { height: footerOffset } = useElementBounding(footerContainer);

const handleContinue = () => {
  if (option.value === 'new') {
    emit('start-auto-delivery');
  } else if (typeof option.value === 'object') {
    emit('add-to-existing-delivery', option.value as ProposedDelivery, oneTimeDelivery.value);
  } else {
    emit('add-to-existing-delivery');
  }
};

whenever(
  () => props.nextDeliveries?.length,
  () => {
    const [delivery] = props.nextDeliveries ?? [];
    if (delivery) {
      option.value = delivery;
    }
  },
);

whenever(option, () => {
  oneTimeDelivery.value = false;
});
</script>

<template>
  <div class="relative">
    <fieldset class="flex flex-col gap-3 w-full">
      <legend class="sr-only">Auto-delivery options</legend>
      <RadioGroupItem
        v-if="!nextDeliveries?.length"
        v-model="option"
        data-test="auto-delivery-start-auto-delivery-option"
        :disabled="isLoading"
        name="auto-delivery-selection"
        value="new"
      >
        <div class="flex flex-col py-1 w-full">
          <BaseBodyText class="font-semibold" data-test="start-new-auto-delivery-header"
            >Start a New Auto-Delivery</BaseBodyText
          >
          <SmallBodyText class="font-normal text-neutral-600">
            This subscription will appear and be activated at checkout.
          </SmallBodyText>
          <AutoDeliveryIntervalSelector
            v-model="interval"
            class="mt-3 md:mt-4"
            :disabled="option !== 'new' || isLoading"
            :inert="option !== 'new'"
          />
        </div>
      </RadioGroupItem>
      <AutoDeliveryDeliveriesList
        v-if="nextDeliveries?.length"
        v-model:option="option"
        v-model:oneTimeDelivery="oneTimeDelivery"
        data-test="auto-delivery-next-delivery-option"
        :deliveries="nextDeliveries"
        :isLoading
        :showOneTimeAutoDeliveryCheckbox="true"
      />
      <RadioGroupItem
        v-else-if="showUpsellOption"
        v-model="option"
        data-test="auto-delivery-add-to-existing-option"
        :disabled="isLoading"
        name="auto-delivery-selection"
        value="upsell"
      >
        <div class="flex flex-col py-1 w-full">
          <BaseBodyText class="font-semibold">
            <span class="hidden lg:visible lg:block">
              Add to Your Existing Auto-Delivery Subscriptions
            </span>
            <span class="lg:hidden">Already have an Auto-delivery set up?</span>
          </BaseBodyText>
          <SmallBodyText class="font-normal text-neutral-600">
            Log in to add this item to your repeat deliveries.
          </SmallBodyText>
        </div>
      </RadioGroupItem>
    </fieldset>
    <slot />
    <span class="flex footer-offset" />
    <div
      class="flex fixed right-0 bottom-0 gap-4 justify-end p-4 pb-6 w-full bg-white md:pb-4 md:max-w-lg"
      ref="footerContainer"
    >
      <UnstyledButton
        data-test="auto-delivery-sign-up-learn-more-button"
        :disabled="isLoading"
        @click="$emit('learn-more')"
      >
        <BaseBodyText class="font-semibold text-nuts-sky-800">Learn more</BaseBodyText>
      </UnstyledButton>
      <ThemedButton
        data-test="auto-delivery-sign-up-continue-button"
        :isLoading
        theme="gray"
        @click="handleContinue"
      >
        <BaseBodyText>Continue</BaseBodyText>
      </ThemedButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.footer-offset {
  padding-bottom: v-bind('`${footerOffset}px`');
}

/* Overrides legacy right margin from base.scss */
:deep(label) {
  @apply mr-0;
}
</style>
